// 기능셋/업무배정 기반 권한 플래그 및 탭 접근 유틸리티 훅
// permCodes(Set): 새 통합 권한 시스템 (/api/my/permissions 응답)
// taskCodes, featureSetNames: 기존 시스템 하위호환 유지 (병렬 운영 기간 중 사용)
// workerType: 특정 기능 접근 제한에 사용 (예: 미팅지도 – sales만 허용)
// externalCompany: /api/me 응답의 user.externalCompany (외부직원 소속 회사 정보)
//   { code, name, enabledFeatures } — enabledFeatures.call 로 일일콜현황 탭 표시 결정

// callCompanyCount: /api/me 의 자기 테넌트 일일콜 회사 수 — 정확히 1일 때만 A('tm-admin')를 첫 화면·열기 후보로 둔다(2026-10-14)
export function usePermissions({ permCodes, taskCodes, featureSetNames, workerType, externalCompany, role, callCompanyCount }) {
  // permCodes Set이 있으면 새 시스템, 없으면 기존 배열 기반으로 폴백
  function has(code) {
    if (permCodes instanceof Set) return permCodes.has(code);
    // 기존 하위호환: taskCodes 배열에서 확인
    return Array.isArray(taskCodes) && taskCodes.includes(code);
  }

  function hasFeature(featureName) {
    if (permCodes instanceof Set) {
      // 새 시스템: 기능 코드로 확인
      const featureCodeMap = {
        '일일콜현황':       'TM_FEATURE',
        // '일일콜현황(영화)' 제거: 외부 회사 DOT는 externalCompany.enabledFeatures.call로 처리
        '일일콜현황(제주)': 'TM_FEATURE_JEJU',    // 제주 직원 전용
        '미팅지도':   'MEETING_MAP_FEATURE',
        '출퇴근체크': 'ATTENDANCE_FEATURE',
        '연차확인':   'LEAVE_FEATURE',
        '급여명세서': 'PAYSLIP_FEATURE',
        // TM 직원 전용 기능셋 (관리자 CONTRACT_MANAGEMENT 업무와 완전히 다른 코드)
        '계약현황':           'CONTRACT_FEATURE',
        'N개월 내 계약현황': 'RECENT_CONTRACTS_FEATURE',
        '요약':               'SUMMARY_FEATURE',
        '방문빈도':   'REGION_COVERAGE_FEATURE',
        // 마케터 전용 정산서 기능셋
        '정산서':       'SETTLEMENT_FEATURE',
        '정산서(DOT)':  'SETTLEMENT_DOT_FEATURE',  // DOT 마케터 전용 정산서
        '정산서(LNJ)':  'SETTLEMENT_LNJ_FEATURE',  // LNJ 마케터 전용 정산서
      };
      const code = featureCodeMap[featureName];
      return code ? permCodes.has(code) : false;
    }
    // 기존 하위호환: featureSetNames 배열에서 확인
    return Array.isArray(featureSetNames) && featureSetNames.includes(featureName);
  }

  // ── 기능셋 기반 권한 (기능배정) ─────────────────────────────────────────────
  const canUseMyAttendance            = hasFeature('출퇴근체크');
  const canUseMyLeaves                = hasFeature('연차확인');
  const canUsePayslipFeature          = hasFeature('급여명세서');
  const canUseTmFeatureSet            = hasFeature('일일콜현황');
  // 외부 회사 일일콜현황: 기능셋 배정 기반 (TM_FEATURE_EXT_{CODE})
  // syncExternalCallFeatureSet()으로 생성된 기능셋을 개인별로 배정해야 접근 가능
  // 우선순위: ① externalCompany.code → ② permCodes에서 TM_FEATURE_EXT_* 패턴 검색
  // (worker_type='external'이 아닌 직원에게 외부 콜 기능을 배정한 경우 ②로 처리)
  let extCallPermCode = null;
  let extCallCompanyCode = null; // permCodes 스캔으로 추출한 회사 코드 (externalCompany 없을 때 사용)
  if (externalCompany) {
    // 외부직원 계정: externalCompany.code 기반으로 권한 코드 결정
    extCallCompanyCode = String(externalCompany.code || '').toUpperCase();
    extCallPermCode = `TM_FEATURE_EXT_${extCallCompanyCode}`;
  } else if (permCodes instanceof Set) {
    // 외부직원이 아니지만 TM_FEATURE_EXT_* 기능이 배정된 경우: permCodes에서 직접 탐색
    for (const code of permCodes) {
      if (code.startsWith('TM_FEATURE_EXT_')) {
        extCallPermCode = code;
        extCallCompanyCode = code.replace('TM_FEATURE_EXT_', '');
        break;
      }
    }
  }
  const canUseExternalCallFeature     = extCallPermCode ? has(extCallPermCode) : false;
  let extIncentiveCompanyCode = null;
  if (permCodes instanceof Set) {
    for (const code of permCodes) {
      const match = /^INCENTIVE_([A-Z0-9]+)_MANAGEMENT$/.exec(code);
      if (match) {
        extIncentiveCompanyCode = match[1];
        break;
      }
    }
  }
  const canUseExternalIncentiveManagement = Boolean(extIncentiveCompanyCode);
  // 2026-04-29 분리: DOT/LNJ 별 메뉴 (일일콜현황(외부) 단일 메뉴 → 회사별 분리)
  const canUseTmCallExtDot            = has('TM_FEATURE_EXT_DOT');
  const canUseTmCallExtLnj            = has('TM_FEATURE_EXT_LNJ');
  const canUseTmJejuFeature           = hasFeature('일일콜현황(제주)'); // 제주 직원 전용 기능셋
  // 2026-08-17: workerType==='sales' 제한 제거 — "배정=사용" 원칙. 기능이 배정됐으면 신분과 무관하게
  //   메뉴가 열려야 한다(테넌트 관리자·직원은 external 이라 배정받고도 미팅지도가 안 떴다).
  //   PINE 영향: 미팅지도 기능셋을 배정받았지만 sales 가 아니라 숨겨져 있던 직원(예: 정명철 1명 실측)에게
  //   메뉴가 새로 보인다 — 배정된 사람에게 보이는 것이 원칙상 올바른 상태이며, 원치 않으면 배정을 회수한다.
  const canUseMeetingMap              = hasFeature('미팅지도');
  // TM 직원 전용 기능셋: 관리자 페이지 ContractManagement(CONTRACT_MANAGEMENT 업무)와 완전히 별개
  const canUseContractFeature         = hasFeature('계약현황');
  const canUseRecentContractsFeature  = hasFeature('N개월 내 계약현황');
  const canUseSummaryFeature          = hasFeature('요약');
  const canUseRegionCoverage          = hasFeature('방문빈도');
  // 마케터 전용 정산서 (파인그룹): 담당 매장 관리 + 월별 정산 조회 + 결제 연결
  const canUseSettlementFeature       = hasFeature('정산서');
  // 마케터 전용 정산서 (DOT): 파인그룹 정산서와 동일 구조, DOT 매출 기반
  const canUseSettlementDotFeature    = hasFeature('정산서(DOT)');
  // 마케터 전용 정산서 (LNJ): ext_revenue_card_approvals 카드승인 기반
  const canUseSettlementLnjFeature    = hasFeature('정산서(LNJ)');

  // ── 업무배정 기반 권한 ────────────────────────────────────────────────────
  const canUseUserManagement          = has('USER_MANAGEMENT');
  // canUseExternalUserManagement / canUseExternalLeaveManagement 제거:
  // 외부 회사 관리 탭은 /api/my/external-companies 응답의 accessible_features를 기반으로
  // Dashboard에서 extTabs로 동적 생성됩니다. usePermissions는 관여하지 않습니다.
  const canUseSalesRosterManagement   = has('SALES_ROSTER_MANAGEMENT');
  // canUseIpManagement 제거(2026-10-23): IP 관리는 출퇴근 관리 ☰ 로 옮겨 그 출퇴근 열쇠로 연다.
  const canUseLeaveManagement         = has('LEAVE_MANAGEMENT');
  const canUseAttendanceManagement         = has('ATTENDANCE_MANAGEMENT');
  const canUseAttendanceCalendarManagement = has('ATTENDANCE_CALENDAR_MANAGEMENT'); // 출퇴근달력 관리 업무배정
  const canUseTmCallManagement             = has('TM_CALL_MANAGEMENT');
  // TM_CALL_MANAGEMENT_MOVIE 제거: 외부 회사 DOT는 ext/:code/tm-calls 라우트로 관리
  const canUseTmJejuManagement        = has('TM_CALL_MANAGEMENT_JEJU');  // 일일콜현황(제주) 관리
  // A '(통합)' 화면을 첫 화면으로 고르거나 열 수 있나 — 회사 1곳 테넌트만(회사 2곳+ 는 통합관리로 옮김, 모르면 아님)
  const canOpenTmCallAll              = canUseTmCallManagement && Number(callCompanyCount) === 1;
  const canUseTmCallMenu              = canUseTmFeatureSet || canUseExternalCallFeature || canUseTmCallManagement || canUseTmJejuManagement;
  const canUseMeetingMapManagement    = has('MEETING_MAP_MANAGEMENT');
  const canUseContractManagement      = has('CONTRACT_MANAGEMENT');
  const canUsePromotionalManagement     = has('PROMOTIONAL_MANAGEMENT');
  const canUsePromotionalCostManagement = has('PROMOTIONAL_COST_MANAGEMENT');
  const canUsePromotionalMonthlySettlement = has('PROMOTIONAL_MONTHLY_SETTLEMENT');
  const canUseQuoteManagement           = has('QUOTE_MANAGEMENT');
  const canUseSalesManagement           = has('SALES_MANAGEMENT');      // 입금·세금계산서·카드전화승인·기타
  const canUseSalesCardManagement       = has('SALES_CARD_MANAGEMENT'); // 카드승인 파일 업로드 (민감 데이터)
  const canUseAnyRevenue                = canUseSalesManagement || canUseSalesCardManagement;
  const canUseSettlementManagement      = has('SETTLEMENT_MANAGEMENT'); // 마케터 정산 입금 기록 관리
  const canUsePayoutManagement          = has('PAYOUT_MANAGEMENT') || has('PAYOUT_LNJ_MANAGEMENT') || has('PAYOUT_DOT_MANAGEMENT') || has('SETTLEMENT_MANAGEMENT'); // 정산 관리 (급여이체)
  const canUseReservationManagement     = has('AIRTEL_RESERVATION_MANAGEMENT');
  const canUseReservationRevenue        = has('AIRTEL_RESERVATION_REVENUE');
  const canUseEmergencyTeamManagement   = has('EMERGENCY_TEAM_MANAGEMENT');
  // DOT 정산서·매출 관리 권한 (extTabs에서 visible 여부는 enabled_features로 제어, 이 플래그는 API 접근용)
  const canUseSettlementDotManagement   = has('SETTLEMENT_DOT_MANAGEMENT') || has('SETTLEMENT_MANAGEMENT') || has('SALES_MANAGEMENT');
  const canUseTicketTypeManagement    = has('TICKET_TYPE_MANAGEMENT');
  const canUseInitialSetupManagement  = has('INITIAL_SETUP_MANAGEMENT');
  const canUseSituationBoard          = has('SITUATION_BOARD');
  const canUseSituationBoardManagement = has('SITUATION_BOARD_MANAGEMENT');
  const canUseDbManagement            = has('DB_MANAGEMENT');
  const canUseLogManagement           = has('LOG_MANAGEMENT');
  // EMPLOYEE_VIEW 보유 시 직원페이지 위장 접속 탭 접근 가능
  const canUseEmployeeView            = has('EMPLOYEE_VIEW');
  // WORK_ASSIGNMENT 보유 시 업무/기능 배정 탭 접근 가능
  const canUseWorkAssignment          = has('WORK_ASSIGNMENT');

  // 어떤 탭도 접근 불가한 상태 (메뉴 없음 안내 표시용)
  // hasNoMenu: 외부 회사 동적 탭(extTabs)은 Dashboard에서 별도로 처리하므로 여기서는 static 탭만 체크
  // extTabs가 있으면 Dashboard에서 hasNoMenu 표시를 막아야 함 (아래 참조)
  const hasNoMenu = !canUseMyAttendance && !canUseMyLeaves && !canUsePayslipFeature && !canUseUserManagement &&
    !canUseLeaveManagement &&
    !canUseAttendanceManagement && !canUseAttendanceCalendarManagement && !canUseTmCallMenu && !canUseMeetingMap &&
    !canUseMeetingMapManagement && !canUseContractManagement && !canUsePromotionalManagement &&
    !canUsePromotionalCostManagement && !canUsePromotionalMonthlySettlement && !canUseQuoteManagement && !canUseAnyRevenue && !canUseTicketTypeManagement && !canUseInitialSetupManagement && !canUseSituationBoard && !canUseSituationBoardManagement && !canUseDbManagement && !canUseLogManagement &&
    !canUseWorkAssignment && !canUseContractFeature && !canUseSummaryFeature &&
    !canUseRegionCoverage && !canUseExternalCallFeature && !canUseTmJejuFeature && !canUseEmployeeView &&
    !canUseReservationManagement && !canUseReservationRevenue && !canUseEmergencyTeamManagement &&
    !canUseSettlementFeature && !canUseSettlementManagement && !canUseSettlementDotFeature &&
    !canUseExternalIncentiveManagement &&
    !canUseSettlementDotManagement &&
    !canUseSettlementLnjFeature;

  // 첫 번째로 접근 가능한 탭 키 반환 (권한 없는 탭 폴백용)
  function resolveFirstAvailableMenu() {
    if (canUseMeetingMap) return 'meeting-map';
    if (canUseMyAttendance) return 'attendance';
    if (canUseMyLeaves) return 'leaves';
    if (canUsePayslipFeature) return 'payslips';
    if (canUseTmFeatureSet) return 'tm';
    if (canUseExternalCallFeature) return 'tm-ext-call'; // 외부 회사 일일콜현황
    if (canUseTmJejuFeature) return 'tm-jeju';
    if (canOpenTmCallAll) return 'tm-admin';
    if (canUseTmJejuManagement) return 'tm-jeju-admin';
    if (canUseAttendanceManagement) return 'attendance-admin';
    if (canUseAttendanceCalendarManagement) return 'attendance-calendar';
    if (canUseLeaveManagement) return 'leave-admin';
    if (canUseUserManagement) return 'users';
    if (canUseMeetingMapManagement) return 'meeting-map-admin';
    if (canUseContractFeature) return 'tm-contracts';
    if (canUseSummaryFeature) return 'summary';
    if (canUseRegionCoverage) return 'region-coverage';
    if (canUseContractManagement) return 'contracts';
    if (canUsePromotionalManagement) return 'promotional';
    if (canUsePromotionalCostManagement) return 'promotional-cost';
    if (canUsePromotionalMonthlySettlement) return 'promotional-monthly-settlement';
    if (canUseQuoteManagement) return 'quotes';
    if (canUseAnyRevenue) return 'revenue';
    if (canUseTicketTypeManagement) return 'ticket-types';
    if (canUseSituationBoard) return 'board';
    if (canUseSituationBoardManagement) return 'situation-widgets';
    if (canUseDbManagement) return 'db';
    if (canUseLogManagement) return 'logs';
    if (canUseEmployeeView) return 'employee-view';
    if (canUseWorkAssignment) return 'work-assignment';
    if (canUseSettlementManagement) return 'settlement-management';
    if (canUseExternalIncentiveManagement) return `ext-incentive-${extIncentiveCompanyCode}`;
    if (canUseEmergencyTeamManagement) return 'emergency-team';
    if (canUseReservationManagement) return 'reservations';
    if (canUseReservationRevenue) return 'reservation-revenue';
    if (canUseSettlementFeature) return 'settlement';
    if (canUseSettlementDotFeature) return 'settlement-dot';
    if (canUseSettlementLnjFeature) return 'settlement-lnj';
    return 'attendance';
  }

  // 주어진 탭 키에 접근 권한이 있는지 확인
  function canOpenMenu(menuKey) {
    if (menuKey === 'meeting-map')          return canUseMeetingMap;
    if (menuKey === 'attendance')           return canUseMyAttendance;
    if (menuKey === 'leaves')              return canUseMyLeaves;
    if (menuKey === 'payslips')            return canUsePayslipFeature;
    if (menuKey === 'users')               return canUseUserManagement;
    // ext- 접두사 탭: Dashboard의 extTabs로 동적 생성됨 — 존재 자체가 권한 허용 의미
    if (menuKey.startsWith('ext-'))        return true;
    // integrated- 접두사 탭: integratedTabs로 동적 생성됨 — /api/my/integrated-menus가 권한 필터 수행
    if (menuKey.startsWith('integrated-')) return true;
    if (menuKey === 'leave-admin')         return canUseLeaveManagement;
    if (menuKey === 'attendance-admin')    return canUseAttendanceManagement;
    if (menuKey === 'attendance-calendar') return canUseAttendanceCalendarManagement;
    if (menuKey === 'tm')                  return canUseTmFeatureSet;
    if (menuKey === 'tm-ext-call')         return canUseExternalCallFeature; // 외부 회사 일일콜현황
    if (menuKey === 'tm-jeju')             return canUseTmJejuFeature;
    if (menuKey === 'tm-admin')            return canOpenTmCallAll;
    if (menuKey === 'tm-jeju-admin')       return canUseTmJejuManagement;
    if (menuKey === 'meeting-map-admin')   return canUseMeetingMapManagement;
    if (menuKey === 'tm-contracts')        return canUseContractFeature;
    if (menuKey === 'tm-recent-contracts') return canUseRecentContractsFeature;
    if (menuKey === 'summary')             return canUseSummaryFeature;
    if (menuKey === 'contracts')           return canUseContractManagement;
    if (menuKey === 'promotional')         return canUsePromotionalManagement;
    if (menuKey === 'promotional-cost')    return canUsePromotionalCostManagement;
    if (menuKey === 'promotional-monthly-settlement') return canUsePromotionalMonthlySettlement;
    if (menuKey === 'quotes')              return canUseQuoteManagement;
    if (menuKey === 'revenue')             return canUseAnyRevenue;
    if (menuKey === 'ticket-types')        return canUseTicketTypeManagement;
    if (menuKey === 'board')               return canUseSituationBoard;
    if (menuKey === 'situation-widgets')   return canUseSituationBoardManagement;
    if (menuKey === 'db')                  return canUseDbManagement;
    if (menuKey === 'logs')                return canUseLogManagement;
    if (menuKey === 'work-assignment')     return canUseWorkAssignment;
    if (menuKey === 'region-coverage')     return canUseRegionCoverage;
    if (menuKey === 'employee-view')       return canUseEmployeeView;
    if (menuKey === 'settlement-management')  return canUseSettlementManagement;
    if (menuKey === 'emergency-team')         return canUseEmergencyTeamManagement;
    if (menuKey === 'reservations')           return canUseReservationManagement;
    if (menuKey === 'reservation-revenue')    return canUseReservationRevenue;
    if (menuKey === 'settlement')             return canUseSettlementFeature;
    if (menuKey === 'settlement-dot')         return canUseSettlementDotFeature;     // DOT 마케터 정산서
    if (menuKey === 'settlement-lnj')         return canUseSettlementLnjFeature;     // LNJ 마케터 정산서
    if (menuKey === 'dot-settlement-mgmt')    return canUseSettlementDotManagement; // DOT 정산서 관리 (static)
    if (menuKey === 'dot-revenue-mgmt')       return canUseSettlementDotManagement; // DOT 매출 관리 (static)
    return false;
  }

  return {
    canUseMyAttendance, canUseMyLeaves, canUsePayslipFeature,
    canUseUserManagement,
    canUseSalesRosterManagement, canUseLeaveManagement,
    canUseAttendanceManagement, canUseAttendanceCalendarManagement, canUseTmFeatureSet, canUseTmCallManagement,
    canUseTmCallMenu, canUseMeetingMap,
    canUseTmJejuManagement,
    canUseMeetingMapManagement, canUseContractManagement,
    canUsePromotionalManagement, canUsePromotionalCostManagement, canUsePromotionalMonthlySettlement,
    canUseQuoteManagement, canUseSalesManagement, canUseSalesCardManagement, canUseAnyRevenue,
    canUseSettlementManagement, canUsePayoutManagement, canUseReservationManagement, canUseReservationRevenue, canUseEmergencyTeamManagement, canUseSettlementDotManagement,
    canUseTicketTypeManagement, canUseInitialSetupManagement, canUseSituationBoard, canUseSituationBoardManagement, canUseDbManagement, canUseLogManagement,
    canUseWorkAssignment,
    canUseEmployeeView,
    // TM 직원 전용 기능셋 권한 (관리자 CONTRACT_MANAGEMENT와 별개)
    canUseContractFeature, canUseRecentContractsFeature, canUseSummaryFeature, canUseRegionCoverage,
    // 외부 회사 일일콜현황 (enabled_features.call 기반)
    canUseExternalCallFeature, extCallCompanyCode, canUseTmJejuFeature,
    canUseExternalIncentiveManagement, extIncentiveCompanyCode,
    canUseTmCallExtDot, canUseTmCallExtLnj,
    // 마케터 정산서 (파인그룹 + DOT + LNJ)
    canUseSettlementFeature, canUseSettlementDotFeature, canUseSettlementLnjFeature,
    hasNoMenu, resolveFirstAvailableMenu, canOpenMenu,
  };
}
